import type { Layer } from '@geosoft/viewer-sdk';
import type { GTwinRecord, SceneItem, SceneSlot } from '@/sdk-stub/gtwin-sdk';
import { reconcileAlternateGroupVisibility } from '@/components/layers/alternateGroupModel';
import { isTerrainDerivativeLayer } from '@/components/terrain/terrainDerivedLayers';
import { sceneItemToLayer } from './sceneItemToLayer';
import {
  buildPanoramaBubbleLayerNode,
  PANORAMA_BUBBLE_NODE_KIND,
  readPanoramaBubbleStationsFromLayer,
} from './sceneTreePanoramaBubble';

export const SCENE_SLOTS: ReadonlyArray<SceneSlot> = [
  'basemaps',
  'terrain',
  'realityData',
  'engineeringModels',
  'featureLayers',
  'overlays',
];

const SCENE_PACKAGE_SCHEMA = 'geosoft.scene-package/1';
// `SCENE_INDEX_SCHEMA` was previously declared as a runtime constant
// but only consumed via `typeof` — the lint rule (correctly) flagged
// the runtime assignment as unused. Inline the literal type below to
// keep the schema id strictly type-level.

type JsonObject = Record<string, unknown>;

interface RefLike {
  readonly href?: string;
  readonly schema?: string;
}

export interface ScenePackageDocument extends JsonObject {
  readonly schema: typeof SCENE_PACKAGE_SCHEMA;
  readonly packageId: string;
  readonly slots: Partial<Record<SceneSlot, unknown>>;
  readonly layerOrder?: string[];
  readonly sceneIndexRef?: RefLike;
  readonly readiness?: string[];
  readonly warnings?: string[];
  readonly readinessSummary?: JsonObject;
}

export interface SceneIndexNode {
  readonly id: string;
  readonly type: string;
  readonly title?: string;
  readonly sceneItemIds?: string[];
  readonly geometryRefs?: SceneGeometryRef[];
  readonly readiness?: string[];
  readonly intelligenceLevel?: string;
  readonly attributes?: JsonObject;
  readonly extensions?: JsonObject;
}

export interface SceneGeometryRef {
  readonly sceneItemId?: string;
  readonly featureId?: string | null;
  readonly contentUri?: string;
  readonly sourceFeatureId?: string | number;
}

export interface SceneIndexEdge {
  readonly id?: string;
  readonly type: string;
  readonly from: string;
  readonly to: string;
}

export interface CandidateFact {
  readonly id: string;
  readonly subjectNodeId: string;
  readonly predicate: string;
  readonly objectNodeId?: string;
  readonly literalValue?: string;
  readonly factType: string;
  readonly source: 'source-truth' | 'ai-inferred' | 'rule-inferred' | 'user-approved' | 'user-rejected';
  readonly confidence: number;
  readonly provenance?: JsonObject;
  readonly modelVersion?: string;
  readonly ruleVersion?: string;
  readonly reviewStatus: 'pending' | 'approved' | 'rejected' | 'superseded';
  readonly createdAt?: string;
  readonly updatedAt?: string;
  readonly evidenceRefs?: JsonObject[];
}

export interface SimulationReadinessChecklistItem {
  readonly key: string;
  readonly label: string;
  readonly status: 'satisfied' | 'candidate' | 'missing';
  readonly trustedEvidence?: JsonObject[];
  readonly candidateEvidence?: JsonObject[];
}

export interface SimulationReadinessReport extends JsonObject {
  readonly schema: 'geosoft.simulation-readiness-report/1';
  readonly sceneId?: string;
  readonly runId?: string;
  readonly domain: string;
  readonly currentReadinessLevel: string;
  readonly domainChecklist?: SimulationReadinessChecklistItem[];
  readonly missingRequiredFields?: Array<{ readonly key?: string; readonly label?: string; readonly message?: string }>;
  readonly blockingIssues?: Array<{ readonly id?: string; readonly targetId?: string; readonly message?: string; readonly severity?: string }>;
  readonly warnings?: Array<{ readonly id?: string; readonly targetId?: string; readonly message?: string; readonly severity?: string }>;
  readonly candidateFactsThatCouldImproveReadiness?: Array<{
    readonly requirement?: string;
    readonly label?: string;
    readonly message?: string;
    readonly candidateEvidence?: JsonObject[];
  }>;
  readonly trustedFactsUsed?: JsonObject[];
  readonly evidenceLinks?: JsonObject[];
  readonly provenanceSummary?: JsonObject;
}

export interface SceneIndexDocument extends JsonObject {
  readonly schema: 'geosoft.scene-index/1';
  readonly graph?: {
    readonly nodes?: SceneIndexNode[];
    readonly edges?: SceneIndexEdge[];
  };
  readonly candidateFacts?: CandidateFact[];
  readonly readiness?: {
    readonly states?: string[];
    readonly gaps?: Array<{
      readonly id?: string;
      readonly severity?: 'info' | 'warning' | 'blocking';
      readonly message?: string;
      readonly blocks?: string[];
    }>;
  };
}

export interface UnifiedSceneTreeNode {
  readonly id: string;
  readonly label: string;
  readonly kind: string;
  readonly sceneItemIds: readonly string[];
  readonly geometryRefs: readonly SceneGeometryRef[];
  readonly attributes?: JsonObject;
  readonly readiness: readonly string[];
  readonly integrationStates: readonly IntegrationOperationalState[];
  readonly candidateFacts?: readonly CandidateFact[];
  readonly intelligenceLevel?: string;
  readonly rasterTerrainAnalysis?: JsonObject;
  readonly children: readonly UnifiedSceneTreeNode[];
}

export interface IntegrationOperationalState {
  readonly state: 'loading' | 'stale' | 'offline' | 'permission-denied' | 'conflict';
  readonly label: string;
  readonly providerType?: string;
  readonly status?: string;
  readonly permissionState?: string;
}

export interface SceneReadinessNotice {
  readonly id: string;
  readonly severity: 'info' | 'warning' | 'blocking';
  readonly label: string;
}

export interface SceneLayerBuildResult {
  readonly layers: Layer[];
  readonly slotEntries: Array<{ layerId: string; slot: SceneSlot }>;
  readonly visibilityEntries: Array<{ layerId: string; visible: boolean }>;
  readonly activeAlternateByGroup: Map<string, string>;
  readonly tree: UnifiedSceneTreeNode;
  readonly notices: SceneReadinessNotice[];
  readonly scenePackage: ScenePackageDocument | null;
  readonly sceneIndex: SceneIndexDocument | null;
  readonly readinessReport: SimulationReadinessReport | null;
}

export interface LoadedScenePackageRefs {
  readonly scenePackage: ScenePackageDocument | null;
  readonly sceneIndex: SceneIndexDocument | null;
  readonly readinessReport: SimulationReadinessReport | null;
  readonly warnings: string[];
}

export async function loadScenePackageRefs(
  record: GTwinRecord,
  fetchImpl: typeof fetch = fetch,
): Promise<LoadedScenePackageRefs> {
  const warnings: string[] = [];

  // When the gTwin has inline scene items in payload.geospatial.scene slots,
  // those are the canonical source of truth. Skip fetching external
  // packageRef/indexRef URLs — the scene tree and readiness data will be
  // derived from the inline items instead.
  const hasInlineItems = collectPayloadSceneItems(record).length > 0;
  if (hasInlineItems) {
    return { scenePackage: null, sceneIndex: null, readinessReport: null, warnings };
  }

  // Fallback for legacy projects that haven't been promoted yet: fetch
  // scene-package.json and related docs from external refs.
  const inlinePackage = readInlineScenePackage(record);
  const packageRef = inlinePackage ? null : findPackageRef(record);
  const scenePackage = inlinePackage ?? (packageRef ? await fetchRefJson<ScenePackageDocument>(packageRef, fetchImpl, warnings) : null);
  const indexRef = findIndexRef(record, scenePackage);
  const sceneIndex = indexRef ? await fetchRefJson<SceneIndexDocument>(indexRef, fetchImpl, warnings, packageRef?.href) : null;
  const readinessReportRef = findReadinessReportRef(record, scenePackage, sceneIndex);
  const readinessReport = readinessReportRef
    ? await fetchRefJson<SimulationReadinessReport>(readinessReportRef, fetchImpl, warnings, packageRef?.href)
    : null;

  return { scenePackage, sceneIndex, readinessReport, warnings };
}

export function buildSceneLayers(
  record: GTwinRecord,
  loaded: Partial<LoadedScenePackageRefs> = {},
): SceneLayerBuildResult {
  const scenePackage = loaded.scenePackage ?? readInlineScenePackage(record);
  const sceneIndex = loaded.sceneIndex ?? null;
  const readinessReport = loaded.readinessReport ?? null;
  const warnings = loaded.warnings ?? [];

  // Inline payload slots are the canonical source of truth. The external
  // scene-package.json is only used as a fallback for projects that haven't
  // been migrated yet (no inline items but a packageRef exists).
  const inlineItems = collectPayloadSceneItems(record);
  const sourceItems = inlineItems.length > 0
    ? inlineItems
    : scenePackage
      ? collectPackageItems(scenePackage)
      : [];

  const layers: Layer[] = [];
  const slotEntries: Array<{ layerId: string; slot: SceneSlot }> = [];
  const visibilityEntries: Array<{ layerId: string; visible: boolean }> = [];

  for (const entry of sourceItems) {
    const normalized = normalizeSceneItem(entry.item, entry.slot);
    const layer = sceneItemToLayer(normalized, entry.slot);
    layers.push(layer);
    slotEntries.push({ layerId: layer.id, slot: entry.slot });
    visibilityEntries.push({ layerId: layer.id, visible: entry.defaultVisible });
  }

  // PLATEAU presets often expose LOD1 + LOD2 (No Texture) + LOD2 (Textured)
  // for the same feature class. The textured variant is the "hero" layer; the
  // others are performance / fallback alternates. To avoid the user seeing
  // three overlapping copies of the same buildings on first load, demote the
  // LOD1 and No-Texture variants to `defaultVisible: false` whenever a
  // textured LOD2 sibling is present in the same scene. The cards still
  // appear in the panel, so the user can toggle them on manually.
  hideRedundantPlateauLodAlternates(layers, visibilityEntries);
  hideTerrainDerivativeCompanions(layers, visibilityEntries);

  const reconciled = reconcileAlternateGroupVisibility(layers, visibilityEntries);

  return {
    layers,
    slotEntries,
    visibilityEntries: reconciled.visibilityEntries,
    activeAlternateByGroup: reconciled.activeByGroup,
    tree: buildUnifiedSceneTree({ layers, slotEntries, scenePackage, sceneIndex }),
    notices: buildReadinessNotices(scenePackage, sceneIndex, warnings),
    scenePackage,
    sceneIndex,
    readinessReport,
  };
}

function collectPackageItems(scenePackage: ScenePackageDocument): Array<{
  item: JsonObject;
  slot: SceneSlot;
  defaultVisible: boolean;
}> {
  const order = new Map((scenePackage.layerOrder ?? []).map((id, index) => [id, index]));
  const entries: Array<{ item: JsonObject; slot: SceneSlot; defaultVisible: boolean; slotIndex: number }> = [];
  for (const [slotIndex, slot] of SCENE_SLOTS.entries()) {
    const items = asArray(scenePackage.slots?.[slot]);
    for (const raw of items) {
      if (!isObject(raw)) continue;
      entries.push({
        item: raw,
        slot,
        defaultVisible: raw.defaultVisibility !== false && raw.visible !== false,
        slotIndex,
      });
    }
  }

  return entries
    .sort((left, right) => {
      const leftOrder = order.get(String(left.item.id)) ?? readNumber(left.item.order) ?? Number.MAX_SAFE_INTEGER;
      const rightOrder = order.get(String(right.item.id)) ?? readNumber(right.item.order) ?? Number.MAX_SAFE_INTEGER;
      if (leftOrder !== rightOrder) return leftOrder - rightOrder;
      return left.slotIndex - right.slotIndex;
    })
    .map(({ item, slot, defaultVisible }) => ({ item, slot, defaultVisible }));
}

function collectPayloadSceneItems(record: GTwinRecord): Array<{
  item: JsonObject;
  slot: SceneSlot;
  defaultVisible: boolean;
}> {
  const scene = record.payload?.geospatial?.scene as unknown as JsonObject | undefined;
  const entries: Array<{ item: JsonObject; slot: SceneSlot; defaultVisible: boolean }> = [];
  if (!scene) return entries;

  for (const slot of SCENE_SLOTS) {
    const raw = scene[slot];
    const items = slot === 'terrain' && isObject(raw) ? [raw] : asArray(raw);
    for (const item of items) {
      if (!isObject(item)) continue;
      entries.push({
        item,
        slot,
        defaultVisible: item.defaultVisibility !== false && item.visible !== false,
      });
    }
  }
  return entries;
}

/**
 * Classify a PLATEAU building layer by detail tier from its id + label.
 *
 *   `primary-textured` → LOD2 (Textured) — the hero we show by default.
 *   `lod1`             → coarse extrusion fallback (white boxes).
 *   `lod2-no-texture`  → same geometry as primary but untextured (perf alt).
 *   `other`            → not a building layer or no LOD info → no demotion.
 */
function classifyBuildingLodVariant(layer: Layer): 'primary-textured' | 'lod1' | 'lod2-no-texture' | 'other' {
  const text = `${layer.id} ${layer.label ?? ''}`.toLowerCase();
  if (!text.includes('bldg') && !text.includes('building')) return 'other';
  if (/lod\s*1\b/.test(text) && !/lod\s*2|lod\s*3|lod\s*4/.test(text)) return 'lod1';
  if (/(no.?texture|no_texture)/.test(text)) return 'lod2-no-texture';
  if (/lod\s*2/.test(text)) return 'primary-textured';
  return 'other';
}

/**
 * Force LOD1 and LOD2-No-Texture building variants to `visible: false` when a
 * textured LOD2 sibling exists in the same scene. Mutates `visibilityEntries`
 * in place. Other detail tiers and non-building layers are untouched.
 *
 * This keeps the cards in the panel (so the user can still toggle them on)
 * but stops the viewer from rendering three overlapping copies of every
 * building on first load.
 */
function hideRedundantPlateauLodAlternates(
  layers: readonly Layer[],
  visibilityEntries: Array<{ layerId: string; visible: boolean }>,
): void {
  const hasTexturedPrimary = layers.some(
    (layer) => classifyBuildingLodVariant(layer) === 'primary-textured',
  );
  if (!hasTexturedPrimary) return;
  const demotableIds = new Set(
    layers
      .filter((layer) => {
        const variant = classifyBuildingLodVariant(layer);
        return variant === 'lod1' || variant === 'lod2-no-texture';
      })
      .map((layer) => layer.id),
  );
  if (demotableIds.size === 0) return;
  for (const entry of visibilityEntries) {
    if (demotableIds.has(entry.layerId)) entry.visible = false;
  }
}

/**
 * Derived terrain products (hillshade / slope / aspect / nDSM / contours) share
 * the parent DEM's exact footprint, and converters mark them
 * `defaultVisibility: true`. Opening them all at once stacks identical
 * rectangles, so the operator only ever sees the topmost one — and hiding it
 * uncovers the next one, which reads as "the hidden layer left its background
 * behind". Keep the cards (and the eye toggle) but open with just the DEM.
 *
 * Mutates `visibilityEntries` in place. No-op when the scene has no parent
 * elevation surface, so a standalone hillshade project still renders.
 */
function hideTerrainDerivativeCompanions(
  layers: readonly Layer[],
  visibilityEntries: Array<{ layerId: string; visible: boolean }>,
): void {
  const derivativeIds = new Set(
    layers.filter((layer) => isTerrainDerivativeLayer(layer)).map((layer) => layer.id),
  );
  if (derivativeIds.size === 0) return;
  const hasParentSurface = layers.some(
    (layer) =>
      !derivativeIds.has(layer.id)
      && (layer.kind === 'terrain' || layer.kind === 'raster')
      && layer.style?.gtwinSlot !== 'basemaps',
  );
  if (!hasParentSurface) return;
  for (const entry of visibilityEntries) {
    if (derivativeIds.has(entry.layerId)) entry.visible = false;
  }
}

function normalizeSceneItem(raw: JsonObject, slot: SceneSlot): SceneItem {
  const endpoints = normalizeEndpoints(raw.endpoints);
  const style = isObject(raw.style) ? { ...raw.style } : {};
  if (typeof raw.artifactKind === 'string') style.artifactKind = raw.artifactKind;
  if (typeof raw.order === 'number') style.order = raw.order;
  if (raw.readiness !== undefined) style.readiness = raw.readiness;
  if (typeof raw.intelligenceLevel === 'string') style.intelligenceLevel = raw.intelligenceLevel;
  if (raw.indexNodeIds !== undefined) style.indexNodeIds = raw.indexNodeIds;
  if (raw.defaultVisibility !== undefined) style.defaultVisibility = raw.defaultVisibility;
  if (typeof raw.title === 'string') style.title = raw.title;
  if (typeof raw.coordinateMode === 'string') style.coordinateMode = raw.coordinateMode;
  if (isObject(raw.extensions)) {
    if (isObject(raw.extensions.rasterTerrainAnalysis)) {
      style.rasterTerrainAnalysis = raw.extensions.rasterTerrainAnalysis;
      style.rasterWorkflowHooks = raw.extensions.rasterTerrainAnalysis.workflowHooks;
    }
    if (isObject(raw.extensions.pointcloud)) {
      style.pointcloud = raw.extensions.pointcloud;
      style.pointcloudWorkflowHooks = raw.extensions.pointcloud.workflowHooks;
    }
    style.scenePackageExtensions = raw.extensions;
  }

  return {
    id: typeof raw.id === 'string' ? raw.id : `${slot}-${Math.random().toString(36).slice(2)}`,
    stacItemId: typeof raw.stacItemId === 'string' ? raw.stacItemId : undefined,
    layerKind: typeof raw.layerKind === 'string' ? raw.layerKind : '',
    crs: typeof raw.crs === 'string' ? raw.crs : typeof raw.sourceCrs === 'string' ? raw.sourceCrs : undefined,
    // INIT-2026-Q3-SCENE-HIERARCHY V-CRS — standard render-frame passthrough.
    sourceCrs: typeof raw.sourceCrs === 'string' ? raw.sourceCrs : undefined,
    displayCrs: typeof raw.displayCrs === 'string' ? raw.displayCrs : undefined,
    displayCrsProj4: typeof raw.displayCrsProj4 === 'string' ? raw.displayCrsProj4 : undefined,
    verticalDatum: typeof raw.verticalDatum === 'string' ? raw.verticalDatum : undefined,
    crsProvenanceUrl: typeof raw.crsProvenanceUrl === 'string' ? raw.crsProvenanceUrl : undefined,
    blockTiled: raw.blockTiled === true ? true : undefined,
    bbox: normalizeBbox(raw.bbox),
    endpoints,
    visible: raw.visible === false || raw.defaultVisibility === false ? false : undefined,
    style,
    derivedFrom: Array.isArray(raw.derivedFrom) ? raw.derivedFrom.filter((value): value is string => typeof value === 'string') : undefined,
    alternateOfLayerId:
      typeof raw.alternateOfLayerId === 'string' || raw.alternateOfLayerId === null
        ? raw.alternateOfLayerId
        : undefined,
    alternateLabel: typeof raw.alternateLabel === 'string' ? raw.alternateLabel : undefined,
    alternatePreference:
      typeof raw.alternatePreference === 'number' && Number.isFinite(raw.alternatePreference)
        ? raw.alternatePreference
        : undefined,
  };
}

function normalizeEndpoints(raw: unknown): Record<string, string> {
  if (!isObject(raw)) return {};
  const endpoints: Record<string, string> = {};
  for (const [key, value] of Object.entries(raw)) {
    if (typeof value === 'string' && value.length > 0) {
      endpoints[key] = value;
    } else if (isObject(value) && typeof value.href === 'string' && value.href.length > 0) {
      endpoints[key] = value.href;
    }
  }
  return endpoints;
}

function buildUnifiedSceneTree(input: {
  layers: readonly Layer[];
  slotEntries: ReadonlyArray<{ layerId: string; slot: SceneSlot }>;
  scenePackage: ScenePackageDocument | null;
  sceneIndex: SceneIndexDocument | null;
}): UnifiedSceneTreeNode {
  if (input.sceneIndex?.graph?.nodes?.length) {
    return buildIndexTree(input.sceneIndex);
  }
  return buildFallbackSlotTree(input.layers, input.slotEntries);
}

function buildIndexTree(sceneIndex: SceneIndexDocument): UnifiedSceneTreeNode {
  const nodes = sceneIndex.graph?.nodes ?? [];
  const candidateFactsByNode = groupCandidateFacts(sceneIndex.candidateFacts ?? []);
  const nodeById = new Map<string, UnifiedSceneTreeNode>();
  for (const node of nodes) {
    nodeById.set(node.id, {
      id: node.id,
      label: node.title ?? node.id,
      kind: node.type,
      sceneItemIds: node.sceneItemIds ?? [],
      geometryRefs: node.geometryRefs ?? [],
      attributes: node.attributes,
      readiness: node.readiness ?? [],
      integrationStates: readIntegrationStates(node),
      candidateFacts: candidateFactsByNode.get(node.id) ?? [],
      intelligenceLevel: node.intelligenceLevel,
      rasterTerrainAnalysis: readRasterTerrainAnalysis(node),
      children: [],
    });
  }

  const childIds = new Set<string>();
  for (const edge of sceneIndex.graph?.edges ?? []) {
    const parentId = edge.type === 'partOf' ? edge.to : edge.from;
    const childId = edge.type === 'partOf' ? edge.from : edge.to;
    if (edge.type !== 'contains' && edge.type !== 'partOf') continue;
    const parent = nodeById.get(parentId);
    const child = nodeById.get(childId);
    if (!parent || !child || parent.id === child.id) continue;
    (parent.children as UnifiedSceneTreeNode[]).push(child);
    childIds.add(child.id);
  }

  const roots = [...nodeById.values()].filter((node) => !childIds.has(node.id));
  const preferredRoots = roots.filter((node) => node.kind === 'scene' || node.kind === 'group');
  const supplementalIntegrationRoots = roots.filter((node) => !preferredRoots.includes(node) && node.kind === 'integration');
  const children = preferredRoots.length > 0 ? [...preferredRoots, ...supplementalIntegrationRoots] : roots;
  return {
    id: sceneIndex.indexId ? String(sceneIndex.indexId) : 'scene-index',
    label: 'Scene index',
    kind: 'root',
    sceneItemIds: [],
    geometryRefs: [],
    readiness: sceneIndex.readiness?.states ?? [],
    integrationStates: [],
    candidateFacts: [],
    children,
  };
}

function buildFallbackSlotTree(
  layers: readonly Layer[],
  slotEntries: ReadonlyArray<{ layerId: string; slot: SceneSlot }>,
): UnifiedSceneTreeNode {
  const slotByLayer = new Map(slotEntries.map((entry) => [entry.layerId, entry.slot]));
  const children: UnifiedSceneTreeNode[] = [];
  for (const slot of SCENE_SLOTS) {
    const slotLayers = layers.filter((layer) => slotByLayer.get(layer.id) === slot);
    if (slotLayers.length === 0) continue;
    children.push({
      id: `slot:${slot}`,
      label: slotLabel(slot),
      kind: 'group',
      sceneItemIds: slotLayers.map((layer) => layer.id),
      geometryRefs: [],
      readiness: [],
      integrationStates: [],
      candidateFacts: [],
      children: slotLayers.map((layer) => {
        if (layer.kind === PANORAMA_BUBBLE_NODE_KIND) {
          const stations = readPanoramaBubbleStationsFromLayer(layer);
          return buildPanoramaBubbleLayerNode(layer, stations);
        }
        return {
          id: `layer:${layer.id}`,
          label: layer.label ?? layer.id,
          kind: layer.kind,
          sceneItemIds: [layer.id],
          geometryRefs: [],
          readiness: readStringArray(layer.style?.readiness),
          integrationStates: readLayerIntegrationStates(layer),
          candidateFacts: [],
          intelligenceLevel: typeof layer.style?.intelligenceLevel === 'string' ? layer.style.intelligenceLevel : undefined,
          rasterTerrainAnalysis: isObject(layer.style?.rasterTerrainAnalysis) ? layer.style.rasterTerrainAnalysis : undefined,
          children: [],
        };
      }),
    });
  }

  return {
    id: 'scene-slots',
    label: 'Scene slots',
    kind: 'root',
    sceneItemIds: [],
    geometryRefs: [],
    readiness: [],
    integrationStates: [],
    candidateFacts: [],
    children,
  };
}

function groupCandidateFacts(candidateFacts: readonly CandidateFact[]): Map<string, CandidateFact[]> {
  const grouped = new Map<string, CandidateFact[]>();
  for (const fact of candidateFacts) {
    if (!fact.subjectNodeId || !fact.id) continue;
    const entries = grouped.get(fact.subjectNodeId) ?? [];
    entries.push(fact);
    grouped.set(fact.subjectNodeId, entries);
  }
  return grouped;
}

function buildReadinessNotices(
  scenePackage: ScenePackageDocument | null,
  sceneIndex: SceneIndexDocument | null,
  warnings: readonly string[],
): SceneReadinessNotice[] {
  const notices: SceneReadinessNotice[] = [];
  if (scenePackage && hasLocalOrUnknownCoordinates(scenePackage)) {
    notices.push({
      id: 'coordinates:local-or-unknown',
      severity: 'warning',
      label: 'Local engineering coordinates / not georeferenced.',
    });
  }
  for (const state of scenePackage?.readiness ?? []) {
    notices.push({ id: `package:${state}`, severity: 'info', label: state });
  }
  for (const state of sceneIndex?.readiness?.states ?? []) {
    notices.push({ id: `index:${state}`, severity: 'info', label: state });
  }
  for (const [index, warning] of [...(scenePackage?.warnings ?? []), ...warnings].entries()) {
    notices.push({ id: `warning:${index}`, severity: 'warning', label: warning });
  }
  for (const gap of sceneIndex?.readiness?.gaps ?? []) {
    notices.push({
      id: gap.id ?? `gap:${notices.length}`,
      severity: gap.severity ?? 'warning',
      label: gap.message ?? `Readiness gap${gap.blocks?.length ? ` blocks ${gap.blocks.join(', ')}` : ''}`,
    });
  }
  for (const node of sceneIndex?.graph?.nodes ?? []) {
    for (const state of readIntegrationStates(node)) {
      notices.push({
        id: `integration:${node.id}:${state.state}`,
        severity: state.state === 'permission-denied' || state.state === 'conflict' ? 'blocking' : 'warning',
        label: `${state.label}: ${node.title ?? node.id}`,
      });
    }
  }
  return notices;
}

function hasLocalOrUnknownCoordinates(scenePackage: ScenePackageDocument): boolean {
  return collectPackageItems(scenePackage).some(({ item }) => {
    const coordinateMode = typeof item.coordinateMode === 'string'
      ? item.coordinateMode
      : isObject(item.style) && typeof item.style.coordinateMode === 'string'
        ? item.style.coordinateMode
        : undefined;
    if (coordinateMode === 'local-engineering' || coordinateMode === 'unknown-local') return true;
    const hasCrs = typeof item.crs === 'string' || typeof item.sourceCrs === 'string';
    const hasBbox = normalizeBbox(item.bbox) !== undefined;
    return !hasCrs && !hasBbox;
  });
}

function findPackageRef(record: GTwinRecord): RefLike | null {
  const geo = record.payload?.geospatial as unknown as JsonObject | undefined;
  const scene = geo?.scene as JsonObject | undefined;
  return readRef(scene?.packageRef)
    ?? readRef(scene?.scenePackageRef)
    ?? readRef(geo?.scenePackageRef)
    ?? latestConversionRef(geo, 'packageRef');
}

function findIndexRef(record: GTwinRecord, scenePackage: ScenePackageDocument | null): RefLike | null {
  const geo = record.payload?.geospatial as unknown as JsonObject | undefined;
  const scene = geo?.scene as JsonObject | undefined;
  return readRef(scenePackage?.sceneIndexRef)
    ?? readRef(scene?.indexRef)
    ?? readRef(scene?.sceneIndexRef)
    ?? readRef(geo?.sceneIndexRef)
    ?? latestConversionRef(geo, 'indexRef');
}

function findReadinessReportRef(
  record: GTwinRecord,
  scenePackage: ScenePackageDocument | null,
  sceneIndex: SceneIndexDocument | null,
): RefLike | null {
  const geo = record.payload?.geospatial as unknown as JsonObject | undefined;
  const scene = geo?.scene as JsonObject | undefined;
  const summary = isObject(scenePackage?.readinessSummary) ? scenePackage.readinessSummary : undefined;
  const packageExtensions = isObject(scenePackage?.extensions) ? scenePackage.extensions : undefined;
  const indexExtensions = isObject(sceneIndex?.extensions) ? sceneIndex.extensions : undefined;
  return readRef(summary?.readinessReportRef)
    ?? readRef(packageExtensions?.readinessReportRef)
    ?? readRef(indexExtensions?.readinessReportRef)
    ?? readRef(scene?.readinessReportRef)
    ?? readRef(geo?.readinessReportRef)
    ?? latestConversionRef(geo, 'readinessReportRef');
}

function latestConversionRef(geo: JsonObject | undefined, key: string): RefLike | null {
  const conversions = asArray(geo?.conversions).filter(isObject);
  for (const conversion of [...conversions].reverse()) {
    const ref = readRef(conversion[key]);
    if (ref) return ref;
  }
  return null;
}

function readInlineScenePackage(record: GTwinRecord): ScenePackageDocument | null {
  const geo = record.payload?.geospatial as unknown as JsonObject | undefined;
  const scene = geo?.scene;
  const candidates = [geo?.scenePackage, isObject(scene) ? scene.package : undefined, scene];
  for (const candidate of candidates) {
    if (isObject(candidate) && candidate.schema === SCENE_PACKAGE_SCHEMA && isObject(candidate.slots)) {
      return candidate as ScenePackageDocument;
    }
  }
  return null;
}

function readRef(raw: unknown): RefLike | null {
  if (typeof raw === 'string' && raw.length > 0) return { href: raw };
  if (isObject(raw) && typeof raw.href === 'string' && raw.href.length > 0) {
    return { href: raw.href, schema: typeof raw.schema === 'string' ? raw.schema : undefined };
  }
  return null;
}

async function fetchRefJson<T extends JsonObject>(
  ref: RefLike,
  fetchImpl: typeof fetch,
  warnings: string[],
  baseHref?: string,
): Promise<T | null> {
  if (!ref.href) return null;
  const href = resolveRefHref(ref.href, baseHref);
  try {
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), 10_000);
    const response = await fetchImpl(href, { signal: controller.signal });
    clearTimeout(timer);
    if (!response.ok) {
      warnings.push(`Could not load ${href}: ${response.status}`);
      return null;
    }
    const json = await response.json();
    return isObject(json) ? (json as T) : null;
  } catch (err) {
    warnings.push(`Could not load ${href}: ${err instanceof Error ? err.message : String(err)}`);
    return null;
  }
}

function resolveRefHref(href: string, baseHref?: string): string {
  if (!baseHref || isAbsoluteHref(href)) return href;
  try {
    return new URL(href, baseHref).toString();
  } catch {
    const separator = baseHref.includes('\\') ? '\\' : '/';
    const baseDir = baseHref.slice(0, Math.max(baseHref.lastIndexOf('/'), baseHref.lastIndexOf('\\')) + 1);
    return `${baseDir || ''}${href.replace(/^[/\\]+/, '').replace(/[\\/]/g, separator)}`;
  }
}

function isAbsoluteHref(href: string): boolean {
  return /^[a-zA-Z][a-zA-Z\d+.-]*:/.test(href) || href.startsWith('/') || href.startsWith('\\\\');
}

function asArray(value: unknown): unknown[] {
  return Array.isArray(value) ? value : [];
}

function isObject(value: unknown): value is JsonObject {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function readNumber(value: unknown): number | undefined {
  return typeof value === 'number' && Number.isFinite(value) ? value : undefined;
}

function normalizeBbox(value: unknown): [number, number, number, number] | undefined {
  if (!Array.isArray(value) || value.length < 4) return undefined;
  const bbox = value.slice(0, 4);
  return bbox.every((entry) => typeof entry === 'number' && Number.isFinite(entry))
    ? [bbox[0], bbox[1], bbox[2], bbox[3]]
    : undefined;
}

function readStringArray(value: unknown): string[] {
  return Array.isArray(value) ? value.filter((entry): entry is string => typeof entry === 'string') : [];
}

function readRasterTerrainAnalysis(node: SceneIndexNode): JsonObject | undefined {
  if (isObject(node.attributes?.rasterTerrainAnalysis)) return node.attributes.rasterTerrainAnalysis;
  if (isObject(node.extensions?.rasterTerrainAnalysis)) return node.extensions.rasterTerrainAnalysis;
  return undefined;
}

function readIntegrationStates(node: SceneIndexNode): IntegrationOperationalState[] {
  const states: IntegrationOperationalState[] = [];
  const fromAttributes = integrationStateFromObject(node.attributes);
  if (fromAttributes) states.push(fromAttributes);
  const integrationRef = isObject(node.extensions?.integrationRef) ? node.extensions.integrationRef : undefined;
  const fromRef = integrationStateFromObject(integrationRef);
  if (fromRef && !states.some((state) => state.state === fromRef.state && state.label === fromRef.label)) {
    states.push(fromRef);
  }
  const directState = typeof node.extensions?.operationalState === 'string'
    ? integrationStateFromOperationalState(node.extensions.operationalState)
    : null;
  if (directState && !states.some((state) => state.state === directState.state)) {
    states.push(directState);
  }
  return states;
}

function readLayerIntegrationStates(layer: Layer): IntegrationOperationalState[] {
  const extensions = isObject(layer.style?.scenePackageExtensions) ? layer.style.scenePackageExtensions : undefined;
  const directState = typeof extensions?.operationalState === 'string'
    ? integrationStateFromOperationalState(extensions.operationalState)
    : null;
  const refs = Array.isArray(extensions?.integrationRefs) ? extensions.integrationRefs.filter(isObject) : [];
  const states = refs
    .map((ref) => integrationStateFromObject(ref))
    .filter((state): state is IntegrationOperationalState => Boolean(state));
  return directState && !states.some((state) => state.state === directState.state) ? [directState, ...states] : states;
}

function integrationStateFromObject(value: JsonObject | undefined): IntegrationOperationalState | null {
  if (!value) return null;
  const state = operationalState(
    typeof value.permissionState === 'string' ? value.permissionState : undefined,
    typeof value.status === 'string' ? value.status : undefined,
  );
  if (!state) return null;
  return {
    state,
    label: stateLabel(state),
    providerType: typeof value.providerType === 'string' ? value.providerType : undefined,
    status: typeof value.status === 'string' ? value.status : undefined,
    permissionState: typeof value.permissionState === 'string' ? value.permissionState : undefined,
  };
}

function integrationStateFromOperationalState(value: string): IntegrationOperationalState | null {
  const state = operationalState(undefined, value);
  return state ? { state, label: stateLabel(state), status: value } : null;
}

function operationalState(permissionState?: string, status?: string): IntegrationOperationalState['state'] | null {
  const permission = permissionState?.toLowerCase();
  const runtimeStatus = status?.toLowerCase();
  if (permission === 'denied' || permission === 'permission-denied') return 'permission-denied';
  if (runtimeStatus === 'loading') return 'loading';
  if (runtimeStatus === 'stale') return 'stale';
  if (runtimeStatus === 'offline') return 'offline';
  if (runtimeStatus === 'conflict') return 'conflict';
  return null;
}

function stateLabel(state: IntegrationOperationalState['state']): string {
  switch (state) {
    case 'loading':
      return 'Loading';
    case 'stale':
      return 'Stale';
    case 'offline':
      return 'Offline';
    case 'permission-denied':
      return 'Permission denied';
    case 'conflict':
      return 'Conflict';
  }
}

function slotLabel(slot: SceneSlot): string {
  switch (slot) {
    case 'basemaps':
      return 'Base maps';
    case 'terrain':
      return 'Terrain';
    case 'realityData':
      return 'Reality data';
    case 'engineeringModels':
      return 'Engineering models';
    case 'featureLayers':
      return 'Feature layers';
    case 'overlays':
      return 'Overlays';
    case 'environment':
      return 'Environment';
  }
}
